/*
 * HistorialLineaTab.jsx — Estado de cuenta visual de la línea de crédito
 * Feb-2026 · Fase 8+
 *
 * Componente estrictamente aditivo. Construye el estado de cuenta de la línea al
 * vuelo consumiendo endpoints existentes (no persiste nada nuevo, no toca backend):
 *   • linea.{fecha_aprobacion, fecha_suspension, fecha_reactivacion, fecha_cancelacion}
 *   • GET /api/lineas/{id}/disposiciones  — créditos hijos con `estatus_desembolso`
 *   • GET /api/pagos?credito_id={id}       — pagos por disposición con `aplicacion_*`
 *
 * Regla de signo (autorizada por el usuario): el signo refleja el cambio en el
 * DISPONIBLE de la línea. Si el disponible sube → +, si baja → −. Flecha y signo
 * dicen lo mismo:
 *   • Línea autorizada     → +límite          (verde ↑)
 *   • Aprobación Dxx       → −monto_original  (rojo ↓ tenue, compromete línea)
 *   • Desembolso Dxx       →  0               (rojo · sin efecto en disponible)
 *   • Pago Dxx             → +capital_aplicado (verde ↑, solo capital libera línea)
 *   • Suspender/Reactivar/Cancelar → 0        (gris · no cambia disponible)
 *
 * Aprobación y Desembolso son DOS eventos separados por diseño (autorizado por el
 * usuario). Entre ambos pueden pasar días y el operador necesita ver ambos.
 *
 * NO se persiste nada. NO se altera pagos_engine, amort, cobranza ni motor.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Loader2, CheckCircle2, Banknote, ArrowDownCircle, ArrowUpCircle,
  PauseCircle, PlayCircle, XCircle, Clock, ShieldAlert,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmt$ = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("es-MX", {
    style: "currency", currency: "MXN", minimumFractionDigits: 2,
  });

// Fecha con hora (para desempatar movimientos del mismo día en orden real).
// Fecha para mostrar. Si la fecha llega con hora ISO usamos "01 abr 2026, 06:34 p.m.".
// Si llega en formato puro "YYYY-MM-DD" (sin hora) mostramos solo la fecha para no
// exhibir un "12:00 a.m." artificial que vendría de parsear la fecha pura.
const fmtFechaLarga = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const esPura = /^\d{4}-\d{2}-\d{2}$/.test(s);
  try {
    if (esPura) {
      return new Date(s + "T12:00:00").toLocaleDateString("es-MX", {
        year: "numeric", month: "short", day: "2-digit",
      });
    }
    return new Date(s).toLocaleString("es-MX", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

// Timestamp confiable para ordenar. Acepta ISO con hora, fechas puras "YYYY-MM-DD"
// y valores vacíos. Para fechas puras usamos el fin del día local (23:59:59) para
// que un desembolso registrado solo con fecha (sin hora) SIEMPRE quede después
// del evento de aprobación del mismo día que sí trae timestamp con hora exacta.
const toTs = (iso) => {
  if (!iso) return 0;
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + "T23:59:59").getTime();
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
};

// Prioridad de desempate cuando dos movimientos comparten timestamp exacto.
// Autorización < Aprobación < Desembolso < Pago < Estatus.
const _ORDEN_TIPO = {
  auth: 0, aprobacion: 1, desembolso: 2, pago: 3,
  suspension: 4, reactivacion: 4, cancelacion: 4,
};

export default function HistorialLineaTab({ linea }) {
  const [disposiciones, setDisposiciones] = useState([]);
  const [pagosPorCredito, setPagosPorCredito] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorTxt, setErrorTxt] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErrorTxt("");
      try {
        const { data: disps } = await api.get(`/lineas/${linea.id}/disposiciones`);
        if (!alive) return;
        const lista = Array.isArray(disps) ? disps : [];
        setDisposiciones(lista);
        // Pagos por disposición, en paralelo. Un fallo aislado no rompe la vista.
        const pagosRes = await Promise.allSettled(
          lista.map((c) => api.get("/pagos", { params: { credito_id: c.id } }))
        );
        if (!alive) return;
        const map = {};
        lista.forEach((c, i) => {
          const r = pagosRes[i];
          map[c.id] = (r.status === "fulfilled" && Array.isArray(r.value?.data))
            ? r.value.data : [];
        });
        setPagosPorCredito(map);
      } catch (e) {
        if (alive) {
          setErrorTxt(apiError(e));
          toast.error(apiError(e));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [linea.id]);

  // Construcción de movimientos + cálculo del disponible acumulado hacia adelante.
  const { movimientos, resumen } = useMemo(() => {
    const eventos = [];
    const limite = Number(linea.limite_autorizado || 0);

    // 1) Autorización de la línea (siempre existe).
    eventos.push({
      key: "auth",
      fecha_iso: linea.fecha_aprobacion || linea.created_at || linea.fecha_inicio,
      tipo: "Línea autorizada",
      referencia: linea.numero_linea,
      autor: linea.aprobada_por,
      delta: limite,           // suma al disponible
      color: "emerald",
    });

    // 2) Eventos de estatus (suspensión / reactivación / cancelación).
    if (linea.fecha_suspension) {
      eventos.push({
        key: "suspension",
        fecha_iso: linea.fecha_suspension,
        tipo: "Suspendida",
        motivo: linea.motivo_suspension,
        delta: 0,
        color: "amber",
      });
    }
    if (linea.fecha_reactivacion) {
      eventos.push({
        key: "reactivacion",
        fecha_iso: linea.fecha_reactivacion,
        tipo: "Reactivada",
        delta: 0,
        color: "sky",
      });
    }
    if (linea.fecha_cancelacion) {
      eventos.push({
        key: "cancelacion",
        fecha_iso: linea.fecha_cancelacion,
        tipo: "Cancelada",
        motivo: linea.motivo_cancelacion,
        delta: 0,
        color: "rose",
      });
    }

    // 3) Por cada disposición: Aprobación (compromete) + Desembolso (dinero sale).
    //    Los pagos también se agregan aquí, uno por uno, con su desglose.
    disposiciones.forEach((c) => {
      const monto = Number(c.monto_original || 0);
      const numero = c.numero_credito || "";
      const ref = numero.split("-").slice(-1)[0] || "";   // p.ej. "D01"
      const desembolsado = c.estatus_desembolso === "desembolsado";

      // 3a) Aprobación — compromete la línea desde created_at, aunque no se haya
      // desembolsado. Delta = −monto_original (el disponible baja al aprobarse).
      eventos.push({
        key: "aprobacion",
        fecha_iso: c.created_at,
        tipo: `Aprobación ${ref}`,
        referencia: numero,
        delta: -monto,
        color: "amber",
        pendiente: !desembolsado,
        nota: !desembolsado
          ? "Compromete el disponible. Pendiente de Mesa de Control."
          : "Compromete el disponible.",
      });

      // 3b) Desembolso — solo si ya se desembolsó. Delta = 0 (el disponible NO
      // cambia entre aprobación y desembolso; solo cambia la salida del dinero).
      if (desembolsado) {
        eventos.push({
          key: "desembolso",
          fecha_iso: c.fecha_desembolso || c.fecha_disposicion,
          tipo: `Desembolso ${ref}`,
          referencia: numero,
          monto_desembolsado: monto,
          delta: 0,
          color: "rose",
          nota: "Entrega efectiva del dinero al cliente.",
        });
      }

      // 3c) Pagos de la disposición — solo la parte de capital libera línea.
      (pagosPorCredito[c.id] || []).forEach((p) => {
        const capital = Number(p.aplicacion_capital || 0);
        eventos.push({
          key: "pago",
          fecha_iso: p.fecha_pago || p.created_at,
          tipo: `Pago ${ref}`,
          referencia: p.recibo || numero,
          delta: capital,               // +capital sube el disponible
          color: "emerald",
          monto_total: Number(p.monto || 0),
          desglose: {
            capital,
            interes: Number(p.aplicacion_interes || 0),
            iva: Number(p.aplicacion_iva || 0),
            moratorio: Number(p.aplicacion_moratorio || 0),
            iva_moratorio: Number(p.aplicacion_iva_moratorio || 0),
            gastos_cobranza: Number(p.aplicacion_gastos_cobranza || 0),
            iva_gastos_cobranza: Number(p.aplicacion_iva_gastos_cobranza || 0),
          },
        });
      });
    });

    // 4) Orden ASC por (timestamp, prioridad_tipo) para calcular disponible acumulado.
    eventos.sort((a, b) => {
      const dt = toTs(a.fecha_iso) - toTs(b.fecha_iso);
      if (dt !== 0) return dt;
      return (_ORDEN_TIPO[a.key] ?? 9) - (_ORDEN_TIPO[b.key] ?? 9);
    });

    // 5) Aplicar delta acumulado.
    let disponible = 0;
    const conDisponible = eventos.map((ev) => {
      disponible = Math.max(0, disponible + (ev.delta || 0));
      return { ...ev, disponible_despues: Math.round(disponible * 100) / 100 };
    });

    // 6) Resumen de barra visual: 3 franjas.
    //    - Dispuesto REAL (desembolsado): Σ saldo_insoluto de disposiciones
    //      con estatus_desembolso === "desembolsado" y estatus ∈ {Vigente,Vencido}.
    //    - Pendiente (aprobado, no desembolsado): Σ saldo_insoluto de disposiciones
    //      con estatus_desembolso !== "desembolsado" y estatus ∈ {Vigente,Vencido}.
    //    - Disponible: limite − dispuesto − pendiente.
    const _ESTATUS_ACTIVA_CRED = ["Vigente", "Vencido"];
    let dispuestoReal = 0, pendiente = 0;
    disposiciones.forEach((c) => {
      if (!_ESTATUS_ACTIVA_CRED.includes(String(c.estatus || ""))) return;
      const saldo = Number(c.saldo_insoluto || 0);
      if (c.estatus_desembolso === "desembolsado") dispuestoReal += saldo;
      else pendiente += saldo;
    });
    dispuestoReal = Math.round(dispuestoReal * 100) / 100;
    pendiente = Math.round(pendiente * 100) / 100;
    const disponibleFinal = Math.max(0, Math.round((limite - dispuestoReal - pendiente) * 100) / 100);

    // 7) Renderizar DESC (más reciente arriba).
    conDisponible.reverse();
    return {
      movimientos: conDisponible,
      resumen: { limite, dispuesto_real: dispuestoReal, pendiente, disponible: disponibleFinal },
    };
  }, [linea, disposiciones, pagosPorCredito]);

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8"
           data-testid="historial-linea-loading">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Construyendo estado de cuenta…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="historial-linea">
      {errorTxt && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
             data-testid="historial-linea-error">
          {errorTxt}
        </div>
      )}

      <BarraUsoLinea resumen={resumen} />

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Movimientos de la línea
          </p>
          <p className="text-[11px] text-slate-400" data-testid="historial-linea-count">
            {movimientos.length} movimiento{movimientos.length === 1 ? "" : "s"} · orden descendente
          </p>
        </div>

        {movimientos.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">
            Sin movimientos registrados en esta línea.
          </p>
        ) : (
          <ol className="relative ml-3 border-l-2 border-slate-200"
              data-testid="historial-linea-timeline">
            {movimientos.map((mv, i) => (
              <MovimientoItem key={`${mv.key}-${mv.referencia || ""}-${i}`} mv={mv} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Barra visual de uso de la línea (dispuesto real + pendiente + disponible)
// ─────────────────────────────────────────────────────────────────────────────
function BarraUsoLinea({ resumen }) {
  const { limite, dispuesto_real, pendiente, disponible } = resumen;
  const pct = (n) => (limite > 0 ? (n / limite) * 100 : 0);
  const pctDispuesto = pct(dispuesto_real);
  const pctPendiente = pct(pendiente);
  const pctDisponible = pct(disponible);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
         data-testid="historial-linea-barra">
      <div className="flex items-center justify-between text-xs">
        <p className="font-semibold uppercase tracking-wide text-slate-500">
          Uso de la línea
        </p>
        <p className="text-slate-500">
          Límite <span className="font-semibold text-slate-800"
            data-testid="hist-barra-limite">{fmt$(limite)}</span>
        </p>
      </div>

      <div className="mt-3 flex h-4 overflow-hidden rounded-full bg-slate-100">
        {pctDispuesto > 0 && (
          <div className="bg-rose-500" style={{ width: `${pctDispuesto}%` }}
               data-testid="hist-barra-dispuesto" title={`Dispuesto ${fmt$(dispuesto_real)}`} />
        )}
        {pctPendiente > 0 && (
          <div className="bg-amber-400" style={{ width: `${pctPendiente}%` }}
               data-testid="hist-barra-pendiente" title={`Pendiente de desembolso ${fmt$(pendiente)}`} />
        )}
        {pctDisponible > 0 && (
          <div className="bg-emerald-500" style={{ width: `${pctDisponible}%` }}
               data-testid="hist-barra-disponible" title={`Disponible ${fmt$(disponible)}`} />
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <div className="rounded-md border border-rose-200 bg-rose-50 p-2">
          <div className="flex items-center gap-1.5 text-rose-700">
            <span className="h-2 w-2 rounded-full bg-rose-500" />
            <span className="font-semibold uppercase tracking-wide">Dispuesto</span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold text-rose-900"
             data-testid="hist-barra-monto-dispuesto">{fmt$(dispuesto_real)}</p>
          <p className="text-[10px] text-rose-700">{pctDispuesto.toFixed(1)}% del límite</p>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
          <div className="flex items-center gap-1.5 text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <span className="font-semibold uppercase tracking-wide">Pendiente</span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold text-amber-900"
             data-testid="hist-barra-monto-pendiente">{fmt$(pendiente)}</p>
          <p className="text-[10px] text-amber-700">
            {pctPendiente.toFixed(1)}% aprobado, Mesa de Control
          </p>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
          <div className="flex items-center gap-1.5 text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-semibold uppercase tracking-wide">Disponible</span>
          </div>
          <p className="mt-1 font-mono text-sm font-bold text-emerald-900"
             data-testid="hist-barra-monto-disponible">{fmt$(disponible)}</p>
          <p className="text-[10px] text-emerald-700">{pctDisponible.toFixed(1)}% libre</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Renglón individual de la línea de tiempo
// ─────────────────────────────────────────────────────────────────────────────
function MovimientoItem({ mv }) {
  const ICONS = {
    auth: CheckCircle2, aprobacion: Clock, desembolso: ArrowDownCircle,
    pago: ArrowUpCircle, suspension: PauseCircle, reactivacion: PlayCircle,
    cancelacion: XCircle,
  };
  const Icon = ICONS[mv.key] || Banknote;

  // Mapa color → clases Tailwind (explícitas para que JIT las conserve).
  const CLR = {
    emerald: { dot: "bg-emerald-100 border-emerald-300", icon: "text-emerald-600",
               title: "text-emerald-800", amount: "text-emerald-700" },
    rose:    { dot: "bg-rose-100 border-rose-300",       icon: "text-rose-600",
               title: "text-rose-800",    amount: "text-rose-700" },
    amber:   { dot: "bg-amber-100 border-amber-300",     icon: "text-amber-700",
               title: "text-amber-800",   amount: "text-amber-700" },
    sky:     { dot: "bg-sky-100 border-sky-300",         icon: "text-sky-600",
               title: "text-sky-800",     amount: "text-sky-700" },
    slate:   { dot: "bg-slate-100 border-slate-300",     icon: "text-slate-600",
               title: "text-slate-800",   amount: "text-slate-700" },
  };
  const c = CLR[mv.color] || CLR.slate;

  // Signo y flecha: solo dibujamos monto con signo cuando delta ≠ 0.
  const showMonto = Number(mv.delta || 0) !== 0;
  const signoTxt = mv.delta > 0 ? "+" : mv.delta < 0 ? "−" : "";
  const flecha = mv.delta > 0
    ? <ArrowUpCircle className="h-3.5 w-3.5" />
    : mv.delta < 0 ? <ArrowDownCircle className="h-3.5 w-3.5" /> : null;

  return (
    <li className="mb-5 ml-5"
        data-testid={`hist-mv-${mv.key}${mv.referencia ? "-" + mv.referencia : ""}`}>
      <span className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border-2 ${c.dot}`}>
        <Icon className={`h-3.5 w-3.5 ${c.icon}`} />
      </span>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className={`text-sm font-semibold ${c.title}`}>{mv.tipo}</p>
        {mv.pendiente && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                data-testid="hist-badge-pendiente">
            Pendiente de desembolso
          </span>
        )}
        {mv.key === "desembolso" && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
            Salida de efectivo
          </span>
        )}
        <span className="text-[11px] text-slate-400">
          {fmtFechaLarga(mv.fecha_iso)}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-6 gap-y-1">
        {showMonto ? (
          <p className={`flex items-center gap-1 font-mono text-base font-bold ${c.amount}`}>
            {flecha}
            {signoTxt}{fmt$(Math.abs(mv.delta))}
          </p>
        ) : mv.key === "desembolso" ? (
          <p className="flex items-center gap-1 font-mono text-sm font-semibold text-rose-700">
            <ArrowDownCircle className="h-3.5 w-3.5" />
            {fmt$(mv.monto_desembolsado || 0)} entregados
          </p>
        ) : null}

        <p className="text-xs text-slate-500">
          Disponible después:{" "}
          <span className="font-mono font-semibold text-slate-800"
                data-testid={`hist-disponible-${mv.key}${mv.referencia ? "-" + mv.referencia : ""}`}>
            {fmt$(mv.disponible_despues)}
          </span>
        </p>
      </div>

      {(mv.autor || mv.referencia) && (
        <p className="mt-0.5 text-[11px] text-slate-500">
          {mv.referencia && (
            <span className="font-mono">{mv.referencia}</span>
          )}
          {mv.referencia && mv.autor && " · "}
          {mv.autor}
        </p>
      )}

      {mv.motivo && (
        <p className="mt-1 flex items-start gap-1 text-xs italic text-slate-600">
          <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
          &ldquo;{mv.motivo}&rdquo;
        </p>
      )}

      {mv.nota && !mv.motivo && (
        <p className="mt-0.5 text-[11px] text-slate-500">{mv.nota}</p>
      )}

      {mv.desglose && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600"
             data-testid={`hist-desglose-${mv.referencia}`}>
          <span>
            Total pagado <span className="font-mono font-semibold text-slate-800">
              {fmt$(mv.monto_total || 0)}
            </span>
          </span>
          <span className="text-slate-400">·</span>
          <span>capital <span className="font-mono">{fmt$(mv.desglose.capital)}</span></span>
          <span>interés <span className="font-mono">{fmt$(mv.desglose.interes)}</span></span>
          <span>IVA <span className="font-mono">{fmt$(mv.desglose.iva)}</span></span>
          {mv.desglose.moratorio > 0 && (
            <span>moratorio <span className="font-mono">{fmt$(mv.desglose.moratorio)}</span></span>
          )}
          {mv.desglose.gastos_cobranza > 0 && (
            <span>gastos <span className="font-mono">{fmt$(mv.desglose.gastos_cobranza)}</span></span>
          )}
        </div>
      )}

      {mv.key === "pago" && (
        <p className="mt-0.5 text-[11px] text-slate-400">
          Solo la parte de <b>capital</b> libera línea; interés e IVA son ingreso financiero.
        </p>
      )}
    </li>
  );
}
